import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  useFetchSignatureByIdUserView,
  useFetchSignatureByIdVerifed,
  useUpdateSignatureVerifedClient,
  useUpdateSignatureVerifedUser
} from '@/features/signatures/services'
import { SignatureDetails } from './SignatureDetails'
import { VerificationActions } from './VerificationActions'

export type Witness = {
  nome: string
  cpf: string
  maquina: string
  local: string
  assinatura: string
}

export type Cliente = {
  ass_testemunha_cliente_1?: string
  ass_testemunha_cliente_2?: string
  assinatura: string
  cpf: string
  cpf_testemunha_cliente_1?: string
  cpf_testemunha_cliente_2?: string
  createData: string
  deletedData: string | null
  fotoDoc: string
  fotoPessoa: string
  id: number
  id_user: number | null
  isAtivo: boolean
  isModifica: boolean
  isVerifed: boolean
  local: string
  isTestemunha: boolean
  local_1?: string
  local_2?: string
  maquina: string
  maquina_1?: string
  maquina_2?: string
  nome: string
  nome_testemunha_cliente_1?: string
  nome_testemunha_cliente_2?: string
  witness1?: Witness | null
  witness2?: Witness | null
}
export type VerificationSignatureProps = {
  id: string
  role: 'owner' | 'user'
}
export default function VerificationSignaturePage({ id, role }: VerificationSignatureProps) {
  const isOwner = role === 'owner'

  const {
    data: clientCNPJ,
    isLoading: loadingCNPJ,
    error: errorCNPJ
  } = useFetchSignatureByIdVerifed(id, { enabled: isOwner })

  const {
    data: clientUser,
    isLoading: loadingUser,
    error: errorUser
  } = useFetchSignatureByIdUserView(id, { enabled: !isOwner })

  const cliente: Cliente | undefined = isOwner ? (clientCNPJ as Cliente) : (clientUser as Cliente)
  const isLoading = isOwner ? loadingCNPJ : loadingUser
  const error = isOwner ? errorCNPJ : errorUser
  const updateUserMutation = useUpdateSignatureVerifedUser()
  const updateClientMutation = useUpdateSignatureVerifedClient()
  // Estado local só para bloquear ações após uma decisão
  const [actionDone, setActionDone] = useState(false)

  const witnesses = useMemo<Witness[]>(() => {
    if (!cliente) return []

    const fromNested = [cliente.witness1, cliente.witness2].filter((witness): witness is Witness => Boolean(witness))
    if (fromNested.length > 0) return fromNested

    return [
      {
        nome: cliente.nome_testemunha_cliente_1 || '',
        cpf: cliente.cpf_testemunha_cliente_1 || '',
        maquina: cliente.maquina_1 || '',
        local: cliente.local_1 || '',
        assinatura: cliente.ass_testemunha_cliente_1 || ''
      },
      {
        nome: cliente.nome_testemunha_cliente_2 || '',
        cpf: cliente.cpf_testemunha_cliente_2 || '',
        maquina: cliente.maquina_2 || '',
        local: cliente.local_2 || '',
        assinatura: cliente.ass_testemunha_cliente_2 || ''
      }
    ].filter(witness => witness.nome || witness.cpf || witness.assinatura)
  }, [cliente])

  const handleVerify = async () => {
    const mutation = isOwner ? updateClientMutation : updateUserMutation
    const payload = isOwner ? { id_owner: id, verifed: true } : { id_user: id, verifed: true }
    await mutation.mutateAsync(payload as any)
    setActionDone(true)
  }

  const handleDeny = async () => {
    const mutation = isOwner ? updateClientMutation : updateUserMutation
    const payload = isOwner ? { id_owner: id, verifed: false } : { id_user: id, verifed: false }
    await mutation.mutateAsync(payload as any)
    setActionDone(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <p className="text-lg font-semibold text-red-600 text-center">Erro ao carregar assinatura.</p>
      </div>
    )
  }

  if (!cliente) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <p className="text-lg font-semibold text-red-600 text-center">Assinatura não encontrada.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-4 py-6 bg-background">
      <div className="w-full max-w-5xl mx-auto">
        {/* 2) SOMENTE VISUALIZAÇÃO */}
        <SignatureDetails
          ownerName={cliente.nome}
          ownerCpf={cliente.cpf}
          isVerifed={cliente.isVerifed}
          device={cliente.maquina}
          createdAt={cliente.createData}
          location={cliente.local}
          selfieSrc={cliente.fotoPessoa}
          signatureSrc={cliente.assinatura}
          documentSrc={cliente.fotoDoc || '/placeholder.png'}
          witnesses={witnesses}
        />
        {!cliente.isVerifed && !actionDone && <VerificationActions onVerify={handleVerify} onDeny={handleDeny} />}
      </div>
    </div>
  )
}
